import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { getDateFormatByCountry } from "../../../../../helpers/constants/countryDateFormat";

import { ReactComponent as ExportIcon } from "../../../../../assets/images/export.svg";
import {
  Column,
  FormController,
  MuiBox,
  MuiButtonTertiary,
  MuiDateFieldFormController,
  MuiLink,
  MuiSelect,
  MuiTable,
  MuiTextFieldWithSearchIcon,
  MuiTypography,
  RowProps,
  StatusBadge,
} from "../../../../../components";
import {
  MAX_LENGTH_50,
  PAGE_IDS,
  PAGE_URLS,
} from "../../../../../helpers/constants";
import {
  filterListByFilterKeyValue,
  getUniqueFilters,
  getValueOrBlank,
  isNotEmpty,
} from "../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import {
  NotificationStatusReportKey,
  NotificationStatusReportDropdownTypes,
} from "../../../modelsAndSchemas/models";
import { reportsResultsSiteCoreKeys } from "../../../siteCore/SiteCoreKeys";
import { siteCoreContentLoadingSelector } from "../../../../../redux/slices/appState/appStateSelector";
import MuiEllipseLabel from "../../../../../components/ellipseLabel/MuiEllipseLabel";
import { mapNotificationStatusReports } from "../../../helper";
import { NotificationStatusReportSummary } from "../../../../../api/services/reports/models";

const pageId = `${PAGE_IDS.REPORTS}-list`;

const {
  programKey,
  clientName,
  participantName,
  deliveryMarket,
  deliveryOffice,
  programTemplate,
  alternateDisplayName,
  notificationSupportDate,
  programStatus,
  programStatusDisplayValue,
  outreachEligibleStatus,
  primaryManagedStatus,
  secondaryManagedStatus,
  stateRegionProvince,
  primaryEmailId,
  primaryPhoneNumber,
  alternatePhoneNumber,
  registrationStatusNext,
  registrationStatusNextDisplayValue,
} = NotificationStatusReportKey;

interface NotificationStatusReportResultProps {
  notificationStatusReports: NotificationStatusReportSummary[];
  isFilteredNotificationStatusReport: boolean;
  onExport: () => void;
}
export const NotificationStatusReportResult = ({
  notificationStatusReports,
  isFilteredNotificationStatusReport = false,
  onExport,
}: NotificationStatusReportResultProps) => {
  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    getValueOrBlank(loggedInUser?.countryCode),
  );
  const {
    commonLabels,
    useFetchLabel,
    fetchValueByKeyOfDropdown,
    fetchComponentTitle,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const [filteredReports, setFilteredReports] = useState<
    NotificationStatusReportSummary[]
  >([]);

  const [uniqueFiltersOptionObj, setUniqueFiltersOptionObj] =
    useState<NotificationStatusReportDropdownTypes>({
      programStatus: [],
      registrationStatusNext: [],
      outreachEligibleStatus: [],
      primaryManagedStatus: [],
      secondaryManagedStatus: [],
    });

  const { control, getValues, reset } = useForm({
    defaultValues: {
      [programKey]: "",
      [clientName]: "",
      [participantName]: "",
      [deliveryMarket]: "",
      [deliveryOffice]: "",
      [programTemplate]: "",
      [alternateDisplayName]: "",
      [notificationSupportDate]: "",
      [programStatus]: "",
      [programStatusDisplayValue]: "",
      [outreachEligibleStatus]: "",
      [primaryManagedStatus]: "",
      [secondaryManagedStatus]: "",
      [stateRegionProvince]: "",
      [primaryEmailId]: "",
      [primaryPhoneNumber]: "",
      [alternatePhoneNumber]: "",
      [registrationStatusNext]: "",
      [registrationStatusNextDisplayValue]: "",
    },
  });

  //   watch entire form state
  const filters = useWatch({ control });

  const getLabel = useFetchLabel(reportsResultsSiteCoreKeys.header);

  const getValueByKeyOfDropdown = (listKey: string, key: string) =>
    fetchValueByKeyOfDropdown(reportsResultsSiteCoreKeys.header, listKey, key);

  const resetPagination = () => {
    if (tableRef.current) {
      tableRef.current.resetPagination();
    }
  };

  const formattedNotificationReport = useMemo(() => {
    setFilteredReports([]);
    reset();
    if (!notificationStatusReports?.length) return [];
    return mapNotificationStatusReports(
      notificationStatusReports,
      getValueOrBlank(userCountryDateFormat),
      getValueByKeyOfDropdown,
    );
  }, [notificationStatusReports, siteCoreLoading]);

  useEffect(() => {
    const uniqueProgramStatus = getUniqueFilters(
      formattedNotificationReport,
      "programStatusDisplayValue",
    );
    const uniqueRegistrationStatusNext = getUniqueFilters(
      formattedNotificationReport,
      "registrationStatusNextDisplayValue",
    );

    const uniqueOutreachEligibleStatus = getUniqueFilters(
      formattedNotificationReport,
      "outreachEligibleStatus",
    );
    const uniquePrimaryManagedStatus = getUniqueFilters(
      formattedNotificationReport,
      "primaryManagedStatus",
    );

    const uniqueSecondaryManagedStatus = getUniqueFilters(
      formattedNotificationReport,
      "secondaryManagedStatus",
    );
    setUniqueFiltersOptionObj({
      programStatus: uniqueProgramStatus,
      registrationStatusNext: uniqueRegistrationStatusNext,
      outreachEligibleStatus: uniqueOutreachEligibleStatus,
      primaryManagedStatus: uniquePrimaryManagedStatus,
      secondaryManagedStatus: uniqueSecondaryManagedStatus,
    });
  }, [formattedNotificationReport]);

  const appliedNotificationFilters = () => {
    if (!formattedNotificationReport.length) return;
    const filterList = filterListByFilterKeyValue(
      formattedNotificationReport,
      filters,
    ) as NotificationStatusReportSummary[];
    setFilteredReports(filterList);
    resetPagination();
  };

  useEffect(() => {
    appliedNotificationFilters();
  }, [filters]);

  const tableRef = useRef<{ resetPagination: () => void }>(null);

  const renderName = (companyName: string) => {
    return (
      <MuiBox data-testid="link">
        <MuiEllipseLabel
          text={companyName}
          labelTypoVariant="h3"
          placement="bottom-start"
        />
      </MuiBox>
    );
  };

  const tableColumns: Column[] = [
    {
      id: programKey,
      isSortVisible: true,
      label: getLabel(reportsResultsSiteCoreKeys.programResultKey),
      render: (row: RowProps) => (
        <MuiLink
          href={`${PAGE_URLS.VIEW_PROGRAM}/${row["programId"]}`}
          isOpenNewTab
        >
          {renderName(row[programKey])}
        </MuiLink>
      ),
    },
    {
      id: clientName,
      isSortVisible: true,
      label: getLabel(reportsResultsSiteCoreKeys.clientName),
      render: (row: RowProps) => (
        <MuiLink
          href={`${PAGE_URLS.VIEW_CLIENT}/${row["clientId"]}`}
          isOpenNewTab
        >
          {renderName(row[clientName])}
        </MuiLink>
      ),
    },
    {
      id: participantName,
      isSortVisible: true,
      label: getLabel(
        reportsResultsSiteCoreKeys.participantNameResultTableColumn,
      ),
    },
    {
      id: deliveryMarket,
      label: getLabel(reportsResultsSiteCoreKeys.marketOfDeliveryOffice),
      isSortVisible: true,
    },
    {
      id: deliveryOffice,
      label: getLabel(reportsResultsSiteCoreKeys.deliveryOfficeTableColumn),
      isSortVisible: true,
    },
    {
      id: programTemplate,
      isSortVisible: true,
      label: getLabel(
        reportsResultsSiteCoreKeys.programTemplateResultTableColumn,
      ),
    },
    {
      id: alternateDisplayName,
      label: getLabel(reportsResultsSiteCoreKeys.alternateProgramTemplateName),
      isSortVisible: true,
    },
    {
      id: notificationSupportDate,
      label: getLabel(
        reportsResultsSiteCoreKeys.notificationSupportDateTableColumn,
      ),
      isSortVisible: true,
    },
    {
      id: programStatusDisplayValue,
      label: getLabel(
        reportsResultsSiteCoreKeys.programStatusResultTableColumn,
      ),
      render: (row: RowProps) => (
        <StatusBadge
          status={row[programStatus]}
          label={row[programStatusDisplayValue]}
        />
      ),
      isSortVisible: true,
    },
    {
      id: outreachEligibleStatus,
      label: getLabel(
        reportsResultsSiteCoreKeys.outreachEligibleStatusTableColumn,
      ),
      isSortVisible: true,
    },
    {
      id: primaryManagedStatus,
      label: getLabel(
        reportsResultsSiteCoreKeys.primaryManagedStatusTableColumn,
      ),
      isSortVisible: true,
    },
    {
      id: secondaryManagedStatus,
      label: getLabel(
        reportsResultsSiteCoreKeys.secondaryManagedStatusTableColumn,
      ),
      isSortVisible: true,
    },
    {
      id: stateRegionProvince,
      label: getLabel(
        reportsResultsSiteCoreKeys.stateRegionProvinceTableColumn,
      ),
      isSortVisible: true,
    },
    {
      id: primaryEmailId,
      label: getLabel(reportsResultsSiteCoreKeys.primaryEmailIDTableColumn),
      isSortVisible: true,
    },
    {
      id: primaryPhoneNumber,
      label: getLabel(reportsResultsSiteCoreKeys.primaryPhoneNumberTableColumn),
      isSortVisible: true,
    },
    {
      id: alternatePhoneNumber,
      label: getLabel(
        reportsResultsSiteCoreKeys.alternatePhoneNumberTableColumn,
      ),
      isSortVisible: true,
    },
    {
      id: registrationStatusNextDisplayValue,
      label: getLabel(
        reportsResultsSiteCoreKeys.registrationStatusNEXTTableColumn,
      ),
      render: (row: RowProps) => (
        <StatusBadge
          status={row[registrationStatusNext]}
          label={row[registrationStatusNextDisplayValue]}
        />
      ),
      isSortVisible: true,
    },
  ];

  const renderFilterTextField = (name: string) => {
    return (
      <FormController key={name} control={control} name={name}>
        <MuiTextFieldWithSearchIcon
          key={name}
          id={`${pageId}-${name}`}
          aria-label={commonLabels.search}
          searchARIALabel={commonLabels.search}
          data-testid={`${name}-search-field`}
          showCloseIcon={true}
          placeholder={commonLabels.search}
          maxLength={MAX_LENGTH_50}
          requiredARIALabel={commonARIALabel.required}
          shouldTrimSpaces={false}
        />
      </FormController>
    );
  };
  const tableHeaderForm: React.JSX.Element[] = [
    renderFilterTextField(programKey),
    renderFilterTextField(clientName),
    renderFilterTextField(participantName),
    renderFilterTextField(deliveryMarket),
    renderFilterTextField(deliveryOffice),
    renderFilterTextField(programTemplate),
    renderFilterTextField(alternateDisplayName),
    <MuiDateFieldFormController
      control={control}
      key={`${pageId}-${notificationSupportDate}`}
      id={`${pageId}-${notificationSupportDate}`}
      name={notificationSupportDate}
      valueFormat={userCountryDateFormat}
      format={userCountryDateFormat}
      requiredARIALabel={commonARIALabel.required}
      selectDateARIALabel={commonARIALabel.selectDate}
      clearable
      hideCalendarIconOnValue={isNotEmpty(getValues(notificationSupportDate))}
    />,
    <FormController
      control={control}
      name={programStatusDisplayValue}
      key={programStatusDisplayValue}
    >
      <MuiSelect
        label=""
        options={uniqueFiltersOptionObj.programStatus}
        visibleDefaultSelectOption={true}
        id={`${pageId}-${programStatusDisplayValue}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={outreachEligibleStatus}
      key={outreachEligibleStatus}
    >
      <MuiSelect
        label=""
        options={uniqueFiltersOptionObj.outreachEligibleStatus}
        visibleDefaultSelectOption={true}
        id={`${pageId}-${outreachEligibleStatus}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={primaryManagedStatus}
      key={primaryManagedStatus}
    >
      <MuiSelect
        label=""
        options={uniqueFiltersOptionObj.primaryManagedStatus}
        visibleDefaultSelectOption={true}
        id={`${pageId}-${primaryManagedStatus}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={secondaryManagedStatus}
      key={secondaryManagedStatus}
    >
      <MuiSelect
        label=""
        options={uniqueFiltersOptionObj.secondaryManagedStatus}
        visibleDefaultSelectOption={true}
        id={`${pageId}-${secondaryManagedStatus}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    renderFilterTextField(stateRegionProvince),
    renderFilterTextField(primaryEmailId),
    renderFilterTextField(primaryPhoneNumber),
    renderFilterTextField(alternatePhoneNumber),
    <FormController
      control={control}
      name={registrationStatusNextDisplayValue}
      key={registrationStatusNextDisplayValue}
    >
      <MuiSelect
        label=""
        options={uniqueFiltersOptionObj.registrationStatusNext}
        visibleDefaultSelectOption={true}
        id={`${pageId}-${registrationStatusNextDisplayValue}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
  ];

  const tableHeaderLabel = isFilteredNotificationStatusReport
    ? fetchComponentTitle(reportsResultsSiteCoreKeys.header)
    : getLabel(reportsResultsSiteCoreKeys.allProgramsHeader);

  return (
    <MuiBox
      className="result-container"
      data-testid="notification-status-report-result"
    >
      <MuiBox className="result-header">
        <MuiTypography variant="h2SemiBold" className="header-text">
          {tableHeaderLabel}
        </MuiTypography>
        {notificationStatusReports.length > 0 && (
          <MuiTypography variant="h3SemiBold" className="header-count">
            {notificationStatusReports.length}
          </MuiTypography>
        )}
        <MuiBox textAlign="right" flexGrow={1}>
          <MuiButtonTertiary onClick={onExport}>
            <ExportIcon />
            {commonLabels.export}
          </MuiButtonTertiary>
        </MuiBox>
      </MuiBox>

      <MuiTable
        ref={tableRef}
        pageId={pageId}
        tableHeaderForm={tableHeaderForm}
        columns={tableColumns}
        emptyMessage={commonLabels.searchCriteria}
        tableResults={filteredReports as unknown as Record<string, ReactNode>[]}
        noDataInApi={notificationStatusReports.length === 0}
        noResultMessage={commonLabels.noResult}
        priorityResults={[]}
        isShowDashIfValueEmpty
      />
    </MuiBox>
  );
};
